import React, { useMemo, useState } from "react";

import { faEllipsis, faMagnifyingGlass, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { faGlobe } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
	ButtonStandard,
	DropDownMenu,
	Icon,
	InputText,
	type PYColumnDef,
	Skeleton,
	Table,
	Text,
	Tooltip,
	toast,
} from "@performyard/py-components";

import CompetencyPickerModal from "./CompetencyPickerModal";
import RemoveConfirmModal from "./RemoveConfirmModal";

import { useCompetencies } from "../../../../../api/hooks/defs/competencies";
import { usePatchRoleCompetency, useRoleCompetencies } from "../../../../../api/hooks/defs/roles";

import type { RoleCompetenciesResponseJSON } from "../../../../../api/services/defs/roles";

type RoleCompetencyDetail = RoleCompetenciesResponseJSON["competencies"][number];

export interface CompetenciesTabProps {
	roleId: string;
	readOnly?: boolean;
}

const isLoadingRow = (data: Partial<RoleCompetencyDetail>) => !data.competency_id;

const CompetenciesTab: React.FC<CompetenciesTabProps> = ({ roleId, readOnly = false }) => {
	const [search, setSearch] = useState("");
	const [pickerMode, setPickerMode] = useState<"add" | "edit" | null>(null);
	const [editTarget, setEditTarget] = useState<RoleCompetencyDetail | null>(null);
	const [removeTarget, setRemoveTarget] = useState<RoleCompetencyDetail | null>(null);

	const { data, isLoading } = useRoleCompetencies(roleId);
	// Same 500-competency cap as the create/edit form's RoleCompetencyList — see that file for context.
	const { data: allCompetencies } = useCompetencies({ status: "active", limit: 500 });
	const { mutate } = usePatchRoleCompetency(roleId, {
		onSuccess: () => {
			setPickerMode(null);
			setEditTarget(null);
			setRemoveTarget(null);
		},
		onError: (error) => {
			toast.error(
				error instanceof Error ? error.message : "An error occurred while updating the role's competencies."
			);
		},
	});

	const competencies = useMemo<RoleCompetencyDetail[]>(() => data?.competencies ?? [], [data]);

	const filtered = useMemo(() => {
		const trimmed = search.trim().toLowerCase();
		if (!trimmed) return competencies;
		return competencies.filter((c) => c.name.toLowerCase().includes(trimmed));
	}, [competencies, search]);

	const cwOptions = [{ key: "edit", label: "Edit Expected Level" }];
	const nonCwOptions = [
		{ key: "edit", label: "Edit Expected Level" },
		{ key: "remove", label: "Remove" },
	];

	const handleAction = (key: string, row: RoleCompetencyDetail) => {
		if (key === "edit") {
			setEditTarget(row);
			setPickerMode("edit");
		} else if (key === "remove") {
			setRemoveTarget(row);
		}
	};

	const handleAdd = () => {
		setEditTarget(null);
		setPickerMode("add");
	};

	// PatchRoleCompetencyBody types competency_id as ObjectId (post-Zod-transform); the wire format is a
	// string and the backend re-parses, so the unknown cast bridges the type-level gap.
	const handlePickerSubmit = (input: { competency_id: string; expected_level: number | null }) => {
		mutate({
			op: "upsert",
			competency_id: input.competency_id,
			expected_level: input.expected_level,
		} as unknown as Parameters<typeof mutate>[0]);
	};

	const handleRemoveConfirm = () => {
		if (!removeTarget) return;
		mutate({ op: "remove", competency_id: removeTarget.competency_id } as unknown as Parameters<typeof mutate>[0]);
	};

	const columns: PYColumnDef<RoleCompetencyDetail>[] = [
		{
			colId: "name",
			headerName: "Competency",
			flex: 2,
			cellRenderer: (params: { data: RoleCompetencyDetail }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;
				return (
					<div className="flex min-w-0 items-center gap-2">
						<Text size="paragraph-3" weight="regular" dangerouslySetClassName="min-w-0">
							<span className="block py-text-truncate">{row.name}</span>
						</Text>
						{row.is_company_wide && (
							<Tooltip content="Company Wide Competency">
								<span
									data-testid={`cw-icon-${row.competency_id}`}
									className="flex size-4 shrink-0 items-center justify-center"
								>
									<FontAwesomeIcon icon={faGlobe} className="text-sm text-primary-5" />
								</span>
							</Tooltip>
						)}
					</div>
				);
			},
		},
		{
			colId: "expected_level",
			headerName: "Expected Level",
			width: 200,
			valueGetter: (params: { data?: RoleCompetencyDetail }) => params.data?.expected_level ?? null,
			cellRenderer: (params: { data: RoleCompetencyDetail }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-12" />;
				if (row.expected_level === null) {
					return (
						<Text size="paragraph-3" weight="regular" color="neutral-7">
							—
						</Text>
					);
				}
				const lvl = row.levels.find((l) => l.rank === row.expected_level);
				const label = lvl ? lvl.name : `Level ${row.expected_level}`;
				return (
					<Text size="paragraph-3" weight="regular">
						{label}
					</Text>
				);
			},
		},
		{
			colId: "actions",
			headerName: "",
			headerClass: "!pr-1",
			cellClass: "!pr-1",
			sortable: false,
			width: 50,
			resizable: false,
			suppressSizeToFit: true,
			hide: readOnly,
			cellStyle: { display: "flex", alignItems: "center", justifyContent: "center", paddingTop: "8px" },
			cellRenderer: (params: { data: RoleCompetencyDetail }) => {
				const row = params.data;
				if (isLoadingRow(row)) return null;
				return (
					<DropDownMenu
						options={row.is_company_wide ? cwOptions : nonCwOptions}
						onSelect={(key) => handleAction(key, row)}
						placement="bottom-end"
						minWidth={80}
					>
						<Icon icon={faEllipsis} size={24} color="neutral-5" />
					</DropDownMenu>
				);
			},
		},
	];

	return (
		<div className="py-4" data-testid="role-detail-competencies-tab">
			<div className="mb-4 flex items-center justify-between gap-4">
				<div className="w-72">
					<InputText
						id="competency-search"
						value={search}
						onChange={(v) => setSearch(v)}
						placeholder="Search competencies..."
						leftIcon={faMagnifyingGlass}
					/>
				</div>
				{!readOnly && (
					<div>
						<ButtonStandard
							color="primary"
							iconLeft={faPlus}
							onClick={handleAdd}
							data-testid="add-competency-button"
						>
							Add Competency
						</ButtonStandard>
					</div>
				)}
			</div>

			<div
				style={{
					height: isLoading
						? `${44 + 3 * 48}px`
						: filtered.length === 0
							? "356px"
							: `${44 + filtered.length * 48}px`,
				}}
			>
				<Table<RoleCompetencyDetail>
					columnDefs={columns}
					rowData={filtered}
					loading={isLoading}
					getRowId={(params) => params.data.competency_id}
					rowHeight={48}
					defaultColDef={{
						cellStyle: { display: "flex", alignItems: "center" },
						resizable: false,
						suppressMovable: true,
					}}
					emptyStateText="No Competencies Found"
				/>
			</div>

			{pickerMode && (
				<CompetencyPickerModal
					isOpen
					mode={pickerMode}
					roleCompetencies={competencies}
					allActiveCompetencies={allCompetencies?.competencies ?? []}
					editTarget={editTarget ?? undefined}
					onSubmit={handlePickerSubmit}
					onClose={() => {
						setPickerMode(null);
						setEditTarget(null);
					}}
				/>
			)}

			{removeTarget && (
				<RemoveConfirmModal
					isOpen
					competencyName={removeTarget.name}
					onConfirm={handleRemoveConfirm}
					onClose={() => setRemoveTarget(null)}
				/>
			)}
		</div>
	);
};

export default CompetenciesTab;
